import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Wrench, Loader2, Plus } from "lucide-react";
import { HorseArtAdmin } from "@/components/game/HorseArtAdmin";
import { LevelAdmin } from "@/components/game/LevelAdmin";
import { StableArtEditor } from "@/components/game/StableArtEditor";
import { StableUpgradeArt } from "@/components/game/StableUpgrades";
import { PopupAdmin } from "@/components/game/GamePopups";
import { PremiumAdmin, PaymentSettings } from "@/components/game/PremiumShop";
import { FramesAdmin } from "@/components/game/FramesAdmin";
import { ShowsAdmin } from "@/components/game/ShowsAdmin";
import { MenuAdmin } from "@/components/game/MenuAdmin";
import { TackAdmin } from "@/components/game/PerfTack";
import { GameTimeAdmin } from "@/components/game/GameTimeAdmin";
import { BackgroundsAdmin } from "@/components/game/BackgroundsAdmin";
import { AchievementRewardsAdmin } from "@/components/game/AchievementRewardsAdmin";
import { CoinHistory } from "@/components/game/CoinHistory";
import { GlitchDiagnostic } from "@/components/game/GlitchDiagnostic";
import { PremiumPacksAdmin } from "@/components/game/PremiumPacksAdmin";
import { HMAGeneticsAdmin } from "@/components/game/HMAGeneticsAdmin";
import { HMASimAdmin } from "@/components/game/HMASimAdmin";

const TABS = [{ k: "players", l: "Players" }, { k: "time", l: "Time" }, { k: "levels", l: "Levels" }, { k: "shop", l: "Shop" }, { k: "tack", l: "Tack" }, { k: "premium", l: "Premium" }, { k: "packs", l: "Premium Packs" }, { k: "payments", l: "Payments" }, { k: "art", l: "Horse Art" }, { k: "frames", l: "Frames" }, { k: "backgrounds", l: "Backgrounds" }, { k: "achrewards", l: "Achievements" }, { k: "shows", l: "Shows" }, { k: "popups", l: "Popups" }, { k: "menu", l: "Menu" }, { k: "stable", l: "Stable Art" }, { k: "upgrades", l: "Upgrades" }, { k: "hmaaccess", l: "HMA Access" }, { k: "hmagenetics", l: "HMA Genetics" }, { k: "hmasim", l: "HMA Sim" }, { k: "markingbake", l: "Marking Bake" }, { k: "glitch", l: "Glitch Fix" }, { k: "audit", l: "Audit Log" }];
const NEW_ITEM = { id: "", name: "", section: "stall", slot: "", category: "", price: 100, color: "#8a5a2b", rarity: "common" };

const HMAAccessAdmin = () => {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { gameApi.get("/game/admin/hma/access").then((r) => setOpen(r.data.open)).catch(() => setOpen(false)); }, []);
  const toggle = async (next) => {
    setBusy(true);
    try { const { data } = await gameApi.put("/game/admin/hma/access", { open: next }); setOpen(data.open); toast.success(data.open ? "HMAs are now OPEN to players" : "HMAs are now CLOSED (players see Coming Soon)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  if (open === null) return <Loading />;
  return (
    <div className="mt-3 space-y-3" data-testid="hma-access-admin">
      <div className={`rounded-xl border p-4 ${open ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
        <p className="text-sm font-bold" data-testid="hma-access-status">
          {open ? "HMAs are OPEN — players can found and manage herds." : "HMAs are CLOSED — players see a “Coming Soon” screen on the My HMA tab."}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">Keep this OFF while you upload base coat art in production so players don't generate art-less horses. Turn it ON to officially open HMAs.</p>
      </div>
      <button data-testid="hma-access-toggle" disabled={busy} onClick={() => toggle(!open)}
        className={`w-full rounded-xl px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50 ${open ? "bg-rose-600" : "bg-emerald-600"}`}>
        {busy ? "Saving…" : open ? "Close HMAs (show Coming Soon)" : "Open HMAs to players"}
      </button>
    </div>
  );
};

const _hexToRgb = (h) => { const m = (h || "#fffcf4").replace("#", ""); return [0, 2, 4].map((i) => parseInt(m.substr(i, 2), 16) || 0); };
const _rgbToHex = (rgb) => "#" + (rgb || [255, 252, 244]).map((v) => Math.max(0, Math.min(255, v | 0)).toString(16).padStart(2, "0")).join("");

const MarkingBakeAdmin = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [prevBusy, setPrevBusy] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const imgUrl = (p) => (p ? (p.startsWith("http") ? p : `${BACKEND}${p}`) : null);
  const load = () => gameApi.get("/game/admin/hma/marking-bake").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false, ivory: [255, 252, 244], floor: 0.6 }));
  useEffect(() => { load(); }, []);
  const loadPreview = async (bust = false) => {
    setPrevBusy(true);
    try {
      const q = `body_model=warm_springs&base_phenotype=bay&lp=true&tobiano=true${bust ? `&_=${Date.now()}` : ""}`;
      const { data } = await gameApi.get(`/game/hma/marking-preview?${q}`);
      setPreview(data.enabled ? data.image_path : null);
    } catch { setPreview(null); }
    setPrevBusy(false);
  };
  useEffect(() => { if (cfg?.enabled) loadPreview(true); }, [cfg?.enabled]);
  const persist = async (next) => {
    setBusy(true);
    try {
      const { data } = await gameApi.put("/game/admin/hma/marking-bake", { enabled: next.enabled, ivory: next.ivory, floor: next.floor });
      setCfg({ enabled: data.enabled, ivory: data.ivory, floor: data.floor });
      toast.success("Saved");
      if (data.enabled) loadPreview(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const clearCache = async () => { try { const { data } = await gameApi.post("/game/admin/hma/marking-bake/clear-cache"); toast.success(`Cleared ${data.deleted} cached image(s)`); loadPreview(true); } catch { toast.error("Failed"); } };
  const prewarm = async () => { setBusy(true); try { const { data } = await gameApi.post("/game/admin/hma/marking-bake/prewarm"); toast.success(`Prewarmed ${data.unique_recipes} recipe(s) — ${data.baked} baked, ${data.cache_hits} cached, ${data.skipped} skipped`); } catch { toast.error("Failed"); } setBusy(false); };
  if (!cfg) return <Loading />;
  return (
    <div className="mt-3 space-y-3" data-testid="marking-bake-admin">
      <div className={`rounded-xl border p-4 ${cfg.enabled ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
        <p className="text-sm font-bold" data-testid="marking-bake-status">
          {cfg.enabled ? "Marking Bake is ON — horses render as one flattened server-baked image (base → Appaloosa → legs → face → overo → tobiano)." : "Marking Bake is OFF — the standard layered renderer is used (unchanged)."}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">White markings (legs/face/overo/tobiano) are generated dynamically from the solid base coat's shading; Appaloosa keeps its own colors. Newly uploaded marking art participates automatically.</p>
      </div>
      <button data-testid="marking-bake-toggle" disabled={busy} onClick={() => persist({ ...cfg, enabled: !cfg.enabled })}
        className={`w-full rounded-xl px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50 ${cfg.enabled ? "bg-rose-600" : "bg-emerald-600"}`}>
        {busy ? "Saving…" : cfg.enabled ? "Turn Marking Bake OFF" : "Turn Marking Bake ON"}
      </button>

      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-xs font-bold text-primary">Dynamic White Tuning</p>
        <div className="flex items-center gap-3">
          <label className="w-40 text-xs font-bold" htmlFor="marking-bake-ivory">Ivory / highlight color</label>
          <input id="marking-bake-ivory" data-testid="marking-bake-ivory" type="color" value={_rgbToHex(cfg.ivory)}
            onChange={(e) => setCfg({ ...cfg, ivory: _hexToRgb(e.target.value) })} className="h-9 w-16 cursor-pointer rounded border border-border bg-transparent" />
          <span className="font-mono text-[11px] text-muted-foreground">rgb({(cfg.ivory || []).join(", ")})</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="w-40 text-xs font-bold" htmlFor="marking-bake-floor">Shadow floor (min brightness)</label>
          <input id="marking-bake-floor" data-testid="marking-bake-floor" type="range" min={0} max={1} step={0.05} value={cfg.floor}
            onChange={(e) => setCfg({ ...cfg, floor: parseFloat(e.target.value) })} className="flex-1" />
          <span className="w-10 text-right font-mono text-[11px] text-muted-foreground">{Number(cfg.floor).toFixed(2)}</span>
        </div>
        <p className="text-[11px] text-muted-foreground">Lower floor = deeper shadows in white areas; higher = flatter/brighter white. Default ivory (255,252,244), floor 0.60.</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="marking-bake-save" disabled={busy} onClick={() => persist(cfg)} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-50">Save tuning</button>
          <button data-testid="marking-bake-reset" disabled={busy} onClick={() => persist({ ...cfg, ivory: [255, 252, 244], floor: 0.6 })} className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold">Reset to defaults</button>
          <button data-testid="marking-bake-clearcache" onClick={clearCache} className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold">Clear cache</button>
          <button data-testid="marking-bake-prewarm" disabled={busy} onClick={prewarm} className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold">Prewarm all horses</button>
        </div>
      </div>

      <div className="rounded-xl border border-border p-4">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-bold text-primary">Live preview (Warm Springs · Bay + Appaloosa + Tobiano)</p>
          <button data-testid="marking-bake-refresh" onClick={() => loadPreview(true)} className="rounded-lg border border-border px-2 py-1 text-[11px] font-bold">Refresh</button>
        </div>
        {!cfg.enabled ? (
          <p className="text-[11px] text-muted-foreground">Turn Marking Bake ON to preview. (Uses whatever base/marking art currently exists.)</p>
        ) : prevBusy ? (
          <div className="flex items-center gap-2 p-4 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Baking…</div>
        ) : preview ? (
          <img data-testid="marking-bake-preview-img" src={imgUrl(preview)} alt="baked preview" className="mx-auto aspect-video w-full max-w-md rounded-lg border border-border object-contain bg-white" />
        ) : (
          <p className="text-[11px] text-muted-foreground">No preview available yet — the required base/marking art may not be uploaded in this environment.</p>
        )}
      </div>
    </div>
  );
};

const fmtLastPlayed = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  return d < 30 ? `${d}d ago` : new Date(iso).toLocaleDateString();
};

const fmtPlaytime = (sec) => {
  sec = Math.max(0, Math.floor(sec || 0));
  if (sec < 60) return "< 1m";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};


export const GameAdmin = ({ onBack }) => {
  const [tab, setTab] = useState("players");
  const [players, setPlayers] = useState(null);
  const [shop, setShop] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refund, setRefund] = useState({ player_number: "", item_id: "" });
  const [coinGrant, setCoinGrant] = useState({});
  const [pSearch, setPSearch] = useState("");
  const [pHasMore, setPHasMore] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [ni, setNi] = useState(NEW_ITEM);
  const [ledgerPlayer, setLedgerPlayer] = useState(null);

  const loadPlayers = async (opts = {}) => {
    try {
      const skip = opts.append ? (players?.length || 0) : 0;
      const qs = new URLSearchParams({ search: pSearch.trim(), limit: "50", skip: String(skip) }).toString();
      const { data } = await gameApi.get(`/game/admin/players?${qs}`);
      setPlayers(opts.append ? [...(players || []), ...data.players] : data.players);
      setPHasMore(!!data.has_more);
    } catch { if (!opts.append) setPlayers([]); }
  };
  const loadShop = () => gameApi.get("/game/admin/shop").then((r) => setShop(r.data.items)).catch(() => setShop([]));
  const loadAudit = () => gameApi.get("/game/admin/audit").then((r) => setAudit(r.data.logs)).catch(() => setAudit([]));
  useEffect(() => {
    if (tab === "players" && !players) loadPlayers();
    if (tab === "shop" && !shop) loadShop();
    if (tab === "audit") loadAudit();
  }, [tab]); // eslint-disable-line

  const grantCoins = async (num) => {
    const amt = Number(coinGrant[num]);
    if (!amt) { toast.error("Enter a coin amount"); return; }
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/admin/players/grant-coins", { player_number: num, amount: amt });
      toast.success(`${data.granted >= 0 ? "Gave" : "Removed"} ${Math.abs(data.granted)}🪙 · #${num} now has ${data.new_balance}🪙`);
      setCoinGrant({ ...coinGrant, [num]: "" }); loadPlayers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const setStatus = async (num, status) => {
    setBusy(true);
    try { await gameApi.post(`/game/admin/players/${num}/status`, { status }); toast.success(`Player #${num} ${status}`); loadPlayers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const saveItem = async (id, patch) => {
    setBusy(true);
    try { await gameApi.post(`/game/admin/shop/${id}`, patch); toast.success("Saved"); loadShop(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const doRefund = async () => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/admin/refund", { player_number: Number(refund.player_number), item_id: refund.item_id });
      toast.success(`Refunded ${data.refunded} coins`); setRefund({ player_number: "", item_id: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const createItem = async () => {
    setBusy(true);
    try {
      await gameApi.post("/game/admin/create-item", { ...ni, price: Number(ni.price) });
      toast.success("Item added to shop"); setNi(NEW_ITEM); setShowNew(false); loadShop();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const retireItem = async (id, retired) => {
    setBusy(true);
    try { await gameApi.post(`/game/admin/shop/${id}/retire`, { retired }); toast.success(retired ? "Retired" : "Restored"); loadShop(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="pb-10" data-testid="admin-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="admin-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <Wrench className="text-amber-600" size={20} /><h2 className="font-display text-xl font-bold text-primary">Game Management</h2>
      </div>
      <div className="px-4 pt-3">
        <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground" htmlFor="admin-section-select">Manage</label>
        <select id="admin-section-select" data-testid="admin-section-select" value={tab} onChange={(e) => setTab(e.target.value)}
          className="w-full rounded-xl border border-border bg-card px-3 py-3 text-sm font-bold text-primary shadow-sm focus:outline-none focus:ring-2 focus:ring-primary">
          {TABS.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
        </select>

        {tab === "players" && (
          <div className="mt-3 space-y-2" data-testid="admin-players">
            <div className="flex gap-2">
              <input data-testid="admin-player-search" value={pSearch} onChange={(e) => setPSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadPlayers(); }}
                placeholder="Search name, email or #number" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              <button data-testid="admin-player-search-btn" onClick={() => loadPlayers()} className="rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground">Search</button>
            </div>
            {!players ? <Loading /> : players.map((p) => (
              <div key={p.player_number} data-testid={`admin-player-${p.player_number}`} className="rounded-xl border border-border bg-card p-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-primary">#{p.player_number} {p.display_name} {p.role === "game_admin" && <span className="rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-700">Admin</span>}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{p.email} · {p.currency}🪙 · {p.makeovers_completed} makeovers · status: <span className={p.status === "suspended" ? "text-destructive font-bold" : "text-green-700"}>{p.status}</span></p>
                  <p data-testid={`admin-player-activity-${p.player_number}`} className="text-[11px] text-muted-foreground">🕒 Last played: <span className="font-semibold text-foreground/80">{fmtLastPlayed(p.last_active_at)}</span> · Total play: <span className="font-semibold text-foreground/80">{fmtPlaytime(p.total_play_seconds)}</span></p>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-2">
                  <input data-testid={`admin-coin-input-${p.player_number}`} type="number" value={coinGrant[p.player_number] ?? ""}
                    onChange={(e) => setCoinGrant({ ...coinGrant, [p.player_number]: e.target.value })}
                    placeholder="Coins" className="w-20 rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
                  <button data-testid={`admin-give-coins-${p.player_number}`} disabled={busy} onClick={() => grantCoins(p.player_number)}
                    className="shrink-0 rounded-lg bg-amber-500 px-2.5 py-1.5 text-xs font-bold text-white">Give 🪙</button>
                  <button data-testid={`admin-coin-history-${p.player_number}`} onClick={() => setLedgerPlayer(p.player_number)}
                    className="shrink-0 rounded-lg border border-border px-2.5 py-1.5 text-xs font-bold text-primary">History</button>
                  {p.role !== "game_admin" && (p.status === "suspended"
                    ? <button data-testid={`admin-reactivate-${p.player_number}`} disabled={busy} onClick={() => setStatus(p.player_number, "active")} className="ml-auto shrink-0 rounded-lg bg-green-600 px-2.5 py-1.5 text-xs font-bold text-white">Reactivate</button>
                    : <button data-testid={`admin-suspend-${p.player_number}`} disabled={busy} onClick={() => setStatus(p.player_number, "suspended")} className="ml-auto shrink-0 rounded-lg bg-destructive px-2.5 py-1.5 text-xs font-bold text-white">Suspend</button>)}
                </div>
              </div>
            ))}
            {players && players.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No players found.</p>}
            {pHasMore && <button data-testid="admin-players-more" onClick={() => loadPlayers({ append: true })} className="w-full rounded-lg border-2 border-border py-2 text-xs font-bold text-primary">Load more</button>}
          </div>
        )}

        {tab === "shop" && (
          <div className="mt-3" data-testid="admin-shop">
            <button data-testid="admin-additem-toggle" onClick={() => setShowNew((v) => !v)}
              className="mb-2 flex h-9 w-full items-center justify-center gap-1 rounded-xl bg-primary text-xs font-bold text-primary-foreground active:scale-95">
              <Plus size={14} /> {showNew ? "Close" : "Add New Cosmetic Item"}
            </button>
            {showNew && (
              <div data-testid="admin-additem-form" className="mb-3 space-y-2 rounded-xl border border-secondary/40 bg-secondary/5 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="ni-id" value={ni.id} onChange={(e) => setNi({ ...ni, id: e.target.value })} placeholder="item id (e.g. stall_gold)" className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
                  <input data-testid="ni-name" value={ni.name} onChange={(e) => setNi({ ...ni, name: e.target.value })} placeholder="Display name" className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
                  <select data-testid="ni-section" value={ni.section} onChange={(e) => setNi({ ...ni, section: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
                    <option value="tack">Tack (horse)</option>
                    <option value="barn">Barn (scene)</option>
                    <option value="stall">Stall (per horse)</option>
                  </select>
                  <input data-testid="ni-slot" value={ni.slot} onChange={(e) => setNi({ ...ni, slot: e.target.value })} placeholder="slot (e.g. nameplate)" className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
                  <input data-testid="ni-category" value={ni.category} onChange={(e) => setNi({ ...ni, category: e.target.value })} placeholder="Category (optional)" className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
                  <input data-testid="ni-price" type="number" value={ni.price} onChange={(e) => setNi({ ...ni, price: e.target.value })} placeholder="Price" className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
                  <div className="col-span-2 flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-muted-foreground">Color</span>
                    <input data-testid="ni-color" type="color" value={ni.color} onChange={(e) => setNi({ ...ni, color: e.target.value })} className="h-7 w-10 rounded border border-border" />
                    <select data-testid="ni-rarity" value={ni.rarity} onChange={(e) => setNi({ ...ni, rarity: e.target.value })} className="ml-auto rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
                      <option value="common">common</option><option value="rare">rare</option><option value="epic">epic</option>
                    </select>
                  </div>
                </div>
                <button data-testid="admin-additem-submit" disabled={busy || !ni.id || !ni.name || !ni.slot} onClick={createItem}
                  className="h-9 w-full rounded-lg bg-secondary text-xs font-bold text-white disabled:opacity-50 active:scale-95">Create Item</button>
              </div>
            )}
            <div className="mb-3 rounded-xl border border-border bg-card p-3">
              <p className="text-xs font-bold text-primary">Refund a purchase</p>
              <div className="mt-2 flex gap-2">
                <input data-testid="refund-player" value={refund.player_number} onChange={(e) => setRefund({ ...refund, player_number: e.target.value })} placeholder="Player #" className="w-20 rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
                <input data-testid="refund-item" value={refund.item_id} onChange={(e) => setRefund({ ...refund, item_id: e.target.value })} placeholder="item id" className="flex-1 rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
                <button data-testid="refund-btn" disabled={busy} onClick={doRefund} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white">Refund</button>
              </div>
            </div>
            <div className="space-y-2">
              {!shop ? <Loading /> : shop.map((it) => (
                <div key={it.id} data-testid={`admin-shop-${it.id}`} className={`flex items-center gap-2 rounded-xl border border-border bg-card p-2.5 ${it.retired ? "opacity-50" : ""}`}>
                  <div className="h-8 w-8 shrink-0 rounded" style={{ background: it.color }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-bold text-primary">{it.name} {it.custom && <span className="rounded-full bg-secondary/15 px-1.5 text-[9px] font-bold text-secondary">custom</span>}</p>
                    <p className="text-[11px] text-muted-foreground">{it.category} · sold {it.purchases}× {it.disabled && <span className="text-destructive">· disabled</span>}{it.retired && <span className="text-destructive"> · retired</span>}</p>
                  </div>
                  <input data-testid={`admin-price-${it.id}`} type="number" defaultValue={it.effective_price}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== it.effective_price) saveItem(it.id, { price: v }); }}
                    className="w-16 rounded-lg border border-border bg-background px-2 py-1 text-xs" />
                  {it.custom ? (
                    <button data-testid={`admin-retire-${it.id}`} disabled={busy} onClick={() => retireItem(it.id, !it.retired)}
                      className={`shrink-0 rounded-lg px-2 py-1.5 text-[11px] font-bold ${it.retired ? "bg-green-600 text-white" : "bg-destructive text-white"}`}>{it.retired ? "Restore" : "Retire"}</button>
                  ) : (
                    <button data-testid={`admin-toggle-${it.id}`} disabled={busy} onClick={() => saveItem(it.id, { disabled: !it.disabled })}
                      className={`shrink-0 rounded-lg px-2 py-1.5 text-[11px] font-bold ${it.disabled ? "bg-green-600 text-white" : "bg-muted text-muted-foreground"}`}>{it.disabled ? "Enable" : "Disable"}</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "time" && <GameTimeAdmin />}
        {tab === "backgrounds" && <BackgroundsAdmin />}
        {tab === "achrewards" && <AchievementRewardsAdmin />}
        {tab === "art" && <HorseArtAdmin />}        {tab === "frames" && <FramesAdmin />}
        {tab === "shows" && <ShowsAdmin />}
        {tab === "popups" && <PopupAdmin />}
        {tab === "tack" && <TackAdmin />}
        {tab === "menu" && <MenuAdmin />}
        {tab === "levels" && <LevelAdmin />}

        {tab === "premium" && <PremiumAdmin />}
        {tab === "packs" && <PremiumPacksAdmin />}
        {tab === "payments" && <PaymentSettings />}

        {tab === "stable" && <StableArtEditor />}
        {tab === "upgrades" && <StableUpgradeArt />}
        {tab === "hmaaccess" && <HMAAccessAdmin />}
        {tab === "hmagenetics" && <HMAGeneticsAdmin />}
        {tab === "hmasim" && <HMASimAdmin />}
        {tab === "markingbake" && <MarkingBakeAdmin />}
        {tab === "glitch" && <GlitchDiagnostic />}

        {tab === "audit" && (
          <div className="mt-3 space-y-1.5" data-testid="admin-audit">
            {!audit ? <Loading /> : audit.length === 0 ? <p className="text-center text-sm text-muted-foreground">No admin actions yet.</p>
              : audit.map((l, i) => (
                <div key={i} className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs">
                  <span className="font-bold text-secondary">{l.action}</span> by #{l.admin_number} {l.admin_name} · <span className="text-muted-foreground">{(l.at || "").slice(0, 19).replace("T", " ")}</span>
                  <div className="text-[11px] text-muted-foreground">{JSON.stringify(l.detail)}</div>
                </div>
              ))}
          </div>
        )}
      </div>
      <CoinHistory open={ledgerPlayer != null} playerNumber={ledgerPlayer} onClose={() => setLedgerPlayer(null)} />
    </div>
  );
};

const Loading = () => <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</p>;
